/**
 * DeskWorks Remote end-to-end authorization helpers (PROTOCOL.md §5). Uses WebCrypto Ed25519,
 * available in current browsers, Electron and Node 22+. React Native supplies its own signer
 * through the same `Signer` interface.
 */
import type { EnvelopeKind, SignedEnvelope } from './remote.js';

export interface Signer {
  /** Raw 32-byte public key, base64url. */
  publicKey: string;
  sign(data: Uint8Array): Promise<Uint8Array>;
}

const enc = new TextEncoder();

export function b64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromB64url(text: string): Uint8Array<ArrayBuffer> {
  const s = atob(text.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((text.length + 3) % 4));
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

function signingInput(kind: EnvelopeKind, p: string): Uint8Array<ArrayBuffer> {
  return enc.encode(`DWR1:${kind}:${p}`);
}

export async function signEnvelope<T>(kind: EnvelopeKind, payload: T, signer: Signer): Promise<SignedEnvelope> {
  const p = b64url(enc.encode(JSON.stringify(payload)));
  const sig = await signer.sign(signingInput(kind, p));
  return { p, k: signer.publicKey, s: b64url(sig) };
}

/** Verifies an envelope of the expected kind and returns its payload, or null. */
export async function openEnvelope<T>(kind: EnvelopeKind, env: SignedEnvelope): Promise<T | null> {
  try {
    const key = await crypto.subtle.importKey('raw', fromB64url(env.k), { name: 'Ed25519' }, false, ['verify']);
    const ok = await crypto.subtle.verify({ name: 'Ed25519' }, key, fromB64url(env.s), signingInput(kind, env.p));
    if (!ok) return null;
    return JSON.parse(new TextDecoder().decode(fromB64url(env.p))) as T;
  } catch {
    return null;
  }
}

/** Reads a payload without verifying it (display only; never for decisions). */
export function peekEnvelope<T>(env: SignedEnvelope): T | null {
  try {
    return JSON.parse(new TextDecoder().decode(fromB64url(env.p))) as T;
  } catch {
    return null;
  }
}

/** First 16 hex digits of SHA-256 over the raw public key, shown as four groups. */
export async function keyFingerprint(publicKey: string): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', fromB64url(publicKey)));
  return [...digest.slice(0, 8)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function formatFingerprint(fp: string): string {
  return fp.toUpperCase().replace(/(.{4})(?=.)/g, '$1 ');
}

/** The `a=fingerprint:` value of an SDP ("sha-256 AB:CD:…"), or null. */
export function sdpFingerprint(sdp: string): string | null {
  const m = sdp.match(/^a=fingerprint:(\S+) ([0-9A-Fa-f:]+)\s*$/m);
  return m ? `${m[1]!.toLowerCase()} ${m[2]!.toUpperCase()}` : null;
}

export function sameFingerprint(a: string | null, b: string | null): boolean {
  return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
}

export const nowSeconds = () => Math.floor(Date.now() / 1000);
